import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CreditCard, Building2, Wallet, ArrowLeft, Shield, Bitcoin } from 'lucide-react';
import { Button } from '@vaiu/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Badge } from '@vaiu/ui/badge';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { Progress } from '@vaiu/ui/progress';
import { PaymentMethod, platformApi } from '@/services/platformApi';
import { i18n } from '@/utils/i18n';
import { useToast } from '@/hooks/use-toast';
import { useConfig } from '@/context/ConfigContext';
import StripeCardForm from '@/components/payment/StripeCardForm';
import StripeProvider from '@/components/payment/StripeProvider';
import StripeBankForm from '@/components/payment/StripeBankForm';
import PlaidBankForm from '@/components/payment/PlaidBankForm';
import StripeBankReturnHandler from '@/components/payment/StripeBankReturnHandler';
import PayPalConnect from '@/components/payment/PayPalConnect';
import BitcoinWalletForm from '@/components/payment/BitcoinWalletForm';

type PaymentMethodType = 'selection' | 'card' | 'bank' | 'paypal' | 'bitcoin';
type BankType = 'us' | 'plaid' | null;

interface PaymentMethodInfo {
  type: PaymentMethodType;
  title: string;
  description: string;
  icon: React.ReactNode;
  features: string[];
  fees: string;
  speed: string;
  recommended?: boolean;
}

const safeInternalReturnPath = (value: string | null): string => {
  if (!value) return '/payment-sources';
  try {
    if (value.startsWith('http://') || value.startsWith('https://')) {
      const parsed = new URL(value);
      const path = `${parsed.pathname}${parsed.search}`;
      return path.startsWith('/') && !path.startsWith('//') ? path : '/payment-sources';
    }
  } catch {
    return '/payment-sources';
  }
  return value.startsWith('/') && !value.startsWith('//') ? value : '/payment-sources';
};

const AddPaymentMethodPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { config } = useConfig();
  const returnPath = safeInternalReturnPath(searchParams.get('returnUrl'));
  const [currentStep, setCurrentStep] = useState<PaymentMethodType>(
    searchParams.get('method') === 'bank' ? 'bank' : 'selection'
  );
  const [bankType, setBankType] = useState<BankType>(null);

  // Detect Stripe redirect return (e.g. after mandate acceptance) - must complete save flow
  const setupIntentClientSecret = searchParams.get('setup_intent_client_secret');
  const redirectStatus = searchParams.get('redirect_status');
  const isStripeBankReturn = !!setupIntentClientSecret && redirectStatus === 'succeeded';
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const bitcoinEnabled = config?.integrations?.bitcoinEnabled ?? false;

  // Update progress based on current step
  React.useEffect(() => {
    const progressMap = {
      'selection': 25,
      'card': 50,
      'bank': 50,
      'paypal': 50,
      'bitcoin': 50,
    };
    setProgress(progressMap[currentStep] || 0);
  }, [currentStep]);

  const getPaymentMethods = (): PaymentMethodInfo[] => {
    const methods: PaymentMethodInfo[] = [
      {
        type: 'bank',
        title: i18n.__('vaiu.payment.bank_account'),
        description: i18n.__('vaiu.payment.bank_account_description'),
        icon: <Building2 className="w-6 h-6" />,
        features: [
          i18n.__('vaiu.payment.no_fees'), 
          i18n.__('vaiu.payment.most_secure'), 
          i18n.__('vaiu.payment.ach_transfers')
        ],
        fees: i18n.__('vaiu.payment.free'),
        speed: i18n.__('vaiu.payment.business_days'),
        recommended: true,
      },
      {
        type: 'card',
        title: i18n.__('vaiu.payment.credit_card'),
        description: i18n.__('vaiu.payment.credit_card_description'),
        icon: <CreditCard className="w-6 h-6" />,
        features: [
          i18n.__('vaiu.payment.instant_transfers'), 
          i18n.__('vaiu.payment.widely_accepted'), 
          i18n.__('vaiu.payment.easy_setup')
        ],
        fees: i18n.__('vaiu.payment.card_fee'),
        speed: i18n.__('vaiu.payment.instant'),
      },
      {
        type: 'paypal',
        title: i18n.__('vaiu.payment.paypal'),
        description: i18n.__('addPaymentMethod.paypalDescription'),
        icon: <Wallet className="w-6 h-6 text-blue-600" />,
        features: [
          i18n.__('addPaymentMethod.instantTransfers'),
          i18n.__('addPaymentMethod.paypalBalance'),
          i18n.__('addPaymentMethod.buyerProtection')
        ],
        fees: i18n.__('addPaymentMethod.paypalFee'),
        speed: i18n.__('vaiu.payment.instant'),
      },
    ];

    // Add Bitcoin if enabled
    if (bitcoinEnabled) {
      methods.push({
        type: 'bitcoin',
        title: i18n.__('addPaymentMethod.bitcoinTitle'),
        description: i18n.__('addPaymentMethod.bitcoinDescription'),
        icon: <Bitcoin className="w-6 h-6 text-orange-500" />,
        features: [
          i18n.__('addPaymentMethod.decentralized'),
          i18n.__('addPaymentMethod.global'),
          i18n.__('addPaymentMethod.directWallet')
        ],
        fees: i18n.__('addPaymentMethod.networkFees'),
        speed: i18n.__('addPaymentMethod.variableSpeed'),
      });
    }

    return methods;
  };

  const paymentMethods = getPaymentMethods();

  const handleAddMethod = async (method: PaymentMethod) => {
    setLoading(true);
    setProgress(75);
    try {
      // The method is already saved to backend by the form components
      // Just show success and navigate back
      setProgress(100);
      
      toast({
        title: i18n.__('addPaymentMethod.success'),
        description: `Your ${method.type === 'stripe' ? i18n.__('vaiu.payment.credit_card') : method.type === 'plaid' ? i18n.__('vaiu.payment.bank_account') : i18n.__('vaiu.payment.paypal')} has been connected.`,
      });

      setTimeout(() => {
        navigate(returnPath);
      }, 1000);
    } catch (error: any) {
      setProgress(50);
      toast({
        title: i18n.__('addPaymentMethod.error'),
        description: error.message || i18n.__('addPaymentMethod.errorDesc'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMethodSelect = (methodType: PaymentMethodType) => {
    setCurrentStep(methodType);
    if (methodType === 'bank') {
      setBankType(null); // Reset so we show US Bank? question
    }
  };

  const handleBack = () => {
    if (currentStep === 'selection') {
      navigate(returnPath);
    } else if (searchParams.get('method') === 'bank' && currentStep === 'bank' && bankType === null) {
      navigate(returnPath);
    } else if (currentStep === 'bank' && bankType !== null) {
      setBankType(null); // Back to US Bank? question
    } else {
      setCurrentStep('selection');
      setBankType(null);
    }
  };

  const renderSelectionStep = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mx-auto mb-4">
          <Shield className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-2xl font-bold">{i18n.__('vaiu.payment.add_payment_method')}</h2>
        <p className="text-muted-foreground max-w-md mx-auto">
          {i18n.__('vaiu.payment.choose_funding_description')}
        </p>
      </div>

      {/* Funding Source Options */}
      <div className="space-y-4 max-w-2xl mx-auto">
        {paymentMethods.map((method) => (
          <Card
            key={method.type}
            className="cursor-pointer transition-all hover:shadow-md hover:border-primary/50"
            onClick={() => handleMethodSelect(method.type)}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-primary/10 rounded-full">
                    {method.icon}
                  </div>
                  <div>
                    <CardTitle className="text-lg flex items-center space-x-2">
                      <span>{method.title}</span>
                      {method.recommended && (
                        <Badge variant="secondary" className="text-xs">
                          {i18n.__('addPaymentMethod.recommended')}
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription>{method.description}</CardDescription>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <div className="text-sm font-medium text-muted-foreground mb-1">
                    {i18n.__('addPaymentMethod.fees')}
                  </div>
                  <div className="font-semibold text-green-600">{method.fees}</div>
                </div>
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <div className="text-sm font-medium text-muted-foreground mb-1">
                    {i18n.__('addPaymentMethod.speed')}
                  </div>
                  <div className="font-semibold">{method.speed}</div>
                </div>
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <div className="text-sm font-medium text-muted-foreground mb-1">
                    {i18n.__('addPaymentMethod.security')}
                  </div>
                  <div className="font-semibold flex items-center justify-center">
                    <Shield className="w-4 h-4 mr-1" />
                    {i18n.__('addPaymentMethod.secure')}
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="text-sm font-medium">{i18n.__('addPaymentMethod.features')}</div>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {method.features.map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <div className="w-1.5 h-1.5 bg-primary rounded-full mr-2 flex-shrink-0"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderFormStep = () => {
    const method = paymentMethods.find(m => m.type === currentStep);
    if (!method) return null;

    return (
      <div className="space-y-6 max-w-2xl mx-auto">
        {/* Header with Back Button */}
        <div className="flex items-center space-x-4">
          <Button variant="outline" size="sm" onClick={handleBack} className="p-2">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary/10 rounded-full">
              {method.icon}
            </div>
            <div>
              <h2 className="text-xl font-bold">{method.title}</h2>
              <p className="text-muted-foreground">{method.description}</p>
            </div>
          </div>
        </div>

        {/* Method-specific Form */}
        <Card>
          <CardContent className="pt-6">
            {currentStep === 'card' && (
              <StripeProvider>
                <StripeCardForm
                  onSubmit={handleAddMethod}
                  loading={loading}
                  onCancel={handleBack}
                />
              </StripeProvider>
            )}
            {currentStep === 'bank' && bankType === null && (
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  {i18n.__('addPaymentMethod.usBankQuestion')}
                </p>
                <div className="flex gap-3">
                  <Button
                    variant="default"
                    className="flex-1"
                    onClick={() => setBankType('us')}
                  >
                    {i18n.__('addPaymentMethod.yes')}
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setBankType('plaid')}
                  >
                    {i18n.__('addPaymentMethod.no')}
                  </Button>
                </div>
              </div>
            )}
            {currentStep === 'bank' && bankType === 'us' && (
              <StripeProvider>
                <StripeBankForm
                  onSubmit={handleAddMethod}
                  loading={loading}
                  onCancel={handleBack}
                />
              </StripeProvider>
            )}
            {currentStep === 'bank' && bankType === 'plaid' && (
              <PlaidBankForm
                onSubmit={handleAddMethod}
                loading={loading}
                onCancel={handleBack}
              />
            )}
            {currentStep === 'paypal' && (
              <PayPalConnect
                onSubmit={handleAddMethod}
                loading={loading}
                onCancel={handleBack}
              />
            )}
            {currentStep === 'bitcoin' && (
              <BitcoinWalletForm
                onSubmit={handleAddMethod}
                loading={loading}
                onCancel={handleBack}
              />
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/payment-sources')}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{i18n.__('addPaymentMethod.backToSources')}</span>
            </Button>
            
            {/* Progress Indicator */}
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <span>{currentStep === 'selection' ? i18n.__('addPaymentMethod.step1') : i18n.__('addPaymentMethod.step2')}</span>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <Progress value={progress} className="w-full h-2" />
            <div className="flex justify-between text-xs text-muted-foreground mt-2">
              <span>{i18n.__('vaiu.payment.choose_method')}</span>
              <span>{i18n.__('vaiu.payment.setup')}</span>
              <span>{i18n.__('vaiu.payment.complete')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container max-w-4xl mx-auto px-4 py-8">
        {isStripeBankReturn ? (
          <StripeProvider>
            <StripeBankReturnHandler onComplete={() => {}} />
          </StripeProvider>
        ) : (
          currentStep === 'selection' ? renderSelectionStep() : renderFormStep()
        )}
      </div>
    </div>
  );
};

export default AddPaymentMethodPage; 